import React from 'react'
import { Badge, Pagination, Separator, Table, Td, Th, Tr, Tabs } from '@/shared/ui'
import {
  getAffiliateOverview,
  listAffiliateCommissionsPagedCounted,
  listReferredAccountsPaged,
  countAffiliateCanceledSubscriptions,
  getAffiliatePayoutStats,
  getAffiliateTrialFunnel,
} from '../services/affiliateInsightsService'
import { getAffiliateCommissionBalance } from '../services/affiliateBalanceService'
import AffiliateMasterStatsCards from './AffiliateMasterStatsCards'
import { getCommissionStage } from '../utils/maturity'

type Props = {
  affiliateUserId: string
  refreshKey?: number
  extraBelowCards?: React.ReactNode
}

function cents(v?: number | null) {
  return `R$ ${(Number(v || 0) / 100).toFixed(2)}`
}

function iso(v?: string | null) {
  return v ? new Date(v).toLocaleString() : '-'
}

export default function AffiliateInsightsMaster({ affiliateUserId, refreshKey, extraBelowCards }: Props) {
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [ov, setOv] = React.useState<any | null>(null)
  const [refs, setRefs] = React.useState<any[]>([])
  const [comms, setComms] = React.useState<any[]>([])
  const [refsTotal, setRefsTotal] = React.useState(0)
  const [commsTotal, setCommsTotal] = React.useState(0)
  const [refsPage, setRefsPage] = React.useState(1)
  const [commsPage, setCommsPage] = React.useState(1)
  const pageSize = 25
  const [balance, setBalance] = React.useState<{
    pending_cents: number
    available_cents: number
    reserved_cents: number
    paid_cents: number
    accrued_cents: number
  } | null>(null)
  const [canceledSubs, setCanceledSubs] = React.useState<number>(0)
  const [payoutStats, setPayoutStats] = React.useState<{
    paid_count_month: number
    paid_sum_month_cents: number
    paid_sum_all_time_cents: number
  } | null>(null)
  const [funnel, setFunnel] = React.useState<{
    referred_accounts_total: number
    trial_accounts_total: number
    trial_accounts_active: number
    paid_accounts_converted: number
  } | null>(null)
  const [activeTab, setActiveTab] = React.useState<'affiliations' | 'commissions'>('affiliations')

  React.useEffect(() => {
    setRefsPage(1)
    setCommsPage(1)
    setActiveTab('affiliations')
  }, [affiliateUserId])

  React.useEffect(() => {
    let cancel = false
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const [o, ra, cs, b, csubs, pstats, f] = await Promise.all([
          getAffiliateOverview(affiliateUserId),
          listReferredAccountsPaged(affiliateUserId, refsPage, pageSize),
          listAffiliateCommissionsPagedCounted(affiliateUserId, commsPage, pageSize),
          getAffiliateCommissionBalance(affiliateUserId),
          countAffiliateCanceledSubscriptions(affiliateUserId),
          getAffiliatePayoutStats(affiliateUserId),
          getAffiliateTrialFunnel(affiliateUserId),
        ])
        if (cancel) return
        setOv(o)
        setRefs(ra.rows)
        setRefsTotal(ra.total)
        setComms(cs.rows)
        setCommsTotal(cs.total)
        setBalance(b)
        setCanceledSubs(csubs)
        setPayoutStats(pstats)
        setFunnel(f)
      } catch (e: any) {
        if (!cancel) setError(e?.message || 'Falha ao carregar dados do afiliado')
      } finally {
        if (!cancel) setLoading(false)
      }
    }
    if (affiliateUserId) load()
    return () => {
      cancel = true
    }
  }, [affiliateUserId, refsPage, commsPage, refreshKey])

  const maturedAvailableCents = (balance?.available_cents || 0) + (balance?.reserved_cents || 0)
  const uplineActiveClients = Number((ov as any)?.upline_active_subscriptions_count || 0)

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-2 text-2xl font-semibold">{ov?.affiliate_name || 'Afiliado'}</div>
      <div className="mb-4 text-sm text-foreground/70">{ov?.affiliate_email || '-'}</div>
      <Separator className="my-4" />

      {error && (
        <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">
          {error}
        </div>
      )}

      <AffiliateMasterStatsCards
        availableCents={maturedAvailableCents}
        pendingCents={balance?.pending_cents ?? Number(ov?.commission_pending_cents || 0)}
        totalPaidCents={payoutStats?.paid_sum_all_time_cents ?? 0}
        activeClients={Number(ov?.active_subscriptions_count || 0)}
        uplineActiveClients={uplineActiveClients}
        className="mb-4 lg:grid-cols-5"
      />

      {extraBelowCards && <div className="mt-4">{extraBelowCards}</div>}

      <div className="mt-6">
        <Tabs
          tabs={[
            { value: 'affiliations', label: 'Afiliacoes' },
            { value: 'commissions', label: 'Comissoes recentes' },
          ]}
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as 'affiliations' | 'commissions')}
        >
          {activeTab === 'affiliations' && (
            <div>
              <div className="mb-2 text-base font-medium">Afiliacoes</div>
              <Table>
                <thead>
                  <Tr>
                    <Th>Conta</Th>
                    <Th>Criada em</Th>
                    <Th>Status</Th>
                    <Th>Prox. ciclo</Th>
                  </Tr>
                </thead>
                <tbody>
                  {refs.map((r) => (
                    <Tr key={r.account_id}>
                      <Td>{r.account_name || r.account_id}</Td>
                      <Td>{iso(r.created_at)}</Td>
                      <Td>
                        <Badge variant="soft" color={subColor(r.subscription_status)} className="capitalize">
                          {normalizeSubStatus(r.subscription_status)}
                        </Badge>
                      </Td>
                      <Td>{iso(r.current_period_end)}</Td>
                    </Tr>
                  ))}
                  {refs.length === 0 && (
                    <Tr>
                      <Td colSpan={4} className="py-6 text-center text-sm text-foreground/70">
                        Sem afiliacoes ainda.
                      </Td>
                    </Tr>
                  )}
                </tbody>
              </Table>
              <div className="mt-3 flex justify-end">
                <Pagination
                  page={refsPage}
                  total={Math.max(1, Math.ceil(refsTotal / pageSize))}
                  onChange={setRefsPage}
                />
              </div>
            </div>
          )}

          {activeTab === 'commissions' && (
            <div>
              <div className="mb-2 text-base font-medium">Comissoes recentes</div>
              <Table>
                <thead>
                  <Tr>
                    <Th>Periodo</Th>
                    <Th>Status</Th>
                    <Th>Valor</Th>
                    <Th>Matura em</Th>
                  </Tr>
                </thead>
                <tbody>
                  {comms.map((c) => (
                    <Tr key={c.commission_id}>
                      <Td>
                        {iso(c.period_start)} - {iso(c.period_end)}
                      </Td>
                      <Td>
                        <Badge variant="soft" color={commissionStatusColor(c)} className="capitalize">
                          {commissionStatusLabel(c)}
                        </Badge>
                      </Td>
                      <Td>{cents(c.amount_cents)}</Td>
                      <Td>{iso(c.matures_at)}</Td>
                    </Tr>
                  ))}
                  {comms.length === 0 && (
                    <Tr>
                      <Td colSpan={4} className="py-6 text-center text-sm text-foreground/70">
                        Sem comissoes.
                      </Td>
                    </Tr>
                  )}
                </tbody>
              </Table>
              <div className="mt-3 flex justify-end">
                <Pagination
                  page={commsPage}
                  total={Math.max(1, Math.ceil(commsTotal / pageSize))}
                  onChange={setCommsPage}
                />
              </div>
            </div>
          )}
        </Tabs>
      </div>
    </div>
  )
}

function normalizeSubStatus(raw?: string | null): string {
  const s = String(raw || '').toLowerCase()
  if (!s) return 'Pendente'
  const map: Record<string, string> = {
    active: 'Ativo',
    trialing: 'Em teste',
    past_due: 'Em atraso',
    canceled: 'Cancelada',
    incomplete: 'Incompleta',
    unpaid: 'Nao pago',
    paused: 'Pausada',
    pending: 'Pendente',
  }
  return map[s] || 'Pendente'
}

function subColor(raw?: string | null): any {
  const s = String(raw || '').toLowerCase()
  if (!s || s === 'pending') return 'warning'
  if (s === 'active') return 'success'
  if (s === 'trialing') return 'info'
  if (s === 'past_due') return 'warning'
  if (s === 'canceled') return 'error'
  return 'default'
}

function commissionStatusLabel(c: any): string {
  const stage = getCommissionStage(c)
  if (stage === 'pending') return 'Pendente'
  if (stage === 'matured') return 'Maturado'
  if (stage === 'paid') return 'Pago'
  if (stage === 'canceled') return 'Cancelado'
  return String(c?.status || '-')
}

function commissionStatusColor(c: any): any {
  const stage = getCommissionStage(c)
  if (stage === 'pending') return 'warning'
  if (stage === 'matured') return 'info'
  if (stage === 'paid') return 'success'
  if (stage === 'canceled') return 'error'
  return 'default'
}
